// Single shared axios instance for the whole suite. Apps call `configureHttp`
// once at boot, then subsequent requests pick up the bearer token, X-App-Key,
// and X-Idempotency-Key automatically. `setTokenGetter` is called from a hook
// that runs inside Auth0Provider (see `useAuthTokenWiring`).
//
// Mirrors the behaviour of My.Tms.Old/src/app/api/agent.ts but factored into
// a transport-only layer with no app-specific endpoint knowledge.
import axios, { AxiosHeaders } from 'axios';
import type { AxiosError } from 'axios';
import type { HttpRuntimeConfig, TokenGetter } from './types';

const _runtime: HttpRuntimeConfig & { tokenGetter: TokenGetter } = {
  baseUrl: '',
  appKey: '',
  tokenGetter: async () => undefined,
};

/**
 * Initialise the shared http transport. Call once at app boot AFTER the
 * runtime config has been loaded but BEFORE the first API request fires.
 */
export function configureHttp(cfg: HttpRuntimeConfig): void {
  _runtime.baseUrl = cfg.baseUrl;
  _runtime.appKey = cfg.appKey;
  if (cfg.tokenGetter) _runtime.tokenGetter = cfg.tokenGetter;
}

/** Swap the token getter at runtime — used by the Auth0 wiring hook. */
export function setTokenGetter(fn: TokenGetter): void {
  _runtime.tokenGetter = fn;
}

/**
 * The shared axios instance. Most callers should prefer the typed `request`
 * helpers in `./requests` rather than reaching for this directly.
 */
export const http = axios.create({ timeout: 30_000 });

http.interceptors.request.use(async (cfg) => {
  if (!cfg.baseURL) {
    cfg.baseURL = _runtime.baseUrl;
  }

  const headers = AxiosHeaders.from(cfg.headers);
  headers.set('X-App-Key', _runtime.appKey);

  const token = await _runtime.tokenGetter();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Stamp X-Idempotency-Key on every non-idempotent verb so commands the BE
  // marks as `IIdempotentCommand` (Directory + Tms both honour the same
  // header) get deduped on retry. The `X-` prefix is what the BE
  // `IdempotencyBehaviour` actually reads — sending bare `Idempotency-Key`
  // would silently fail with a 400 "header is required" for any flagged
  // command. Caller-provided keys (e.g. an explicit retry from the FE)
  // take precedence so you can pin a key across attempts.
  const method = (cfg.method ?? 'get').toLowerCase();
  if ((method === 'post' || method === 'put') && !headers.has('X-Idempotency-Key')) {
    headers.set('X-Idempotency-Key', crypto.randomUUID());
  }

  cfg.headers = headers;
  return cfg;
});

http.interceptors.response.use(
  (r) => r,
  (err: AxiosError<{
    error?: string;
    errors?: Record<string, string[]>;
    detail?: string;
    title?: string;
  }>) => {
    // Surface server errors as a friendlier Error message. Three shapes
    // we recognise, in priority order:
    //   1. `error: string` — legacy Result<T> envelopes
    //   2. `errors: { field: string[] }` — ASP.NET ModelState validation
    //   3. `detail: string` — RFC 7807 ProblemDetails (what
    //      ControllerBase.Problem() returns; this is the path our
    //      handlers' Result.Failure(...) take via BaseController.
    //      `title` is the bucket name e.g. "Validation Error" — falls
    //      back to it if `detail` is somehow missing).
    if (err.response?.data) {
      const body = err.response.data;
      if (typeof body.error === 'string') {
        return Promise.reject(new Error(body.error));
      }
      if (body.errors) {
        const flat = Object.values(body.errors).flat().join('; ');
        return Promise.reject(new Error(flat || err.message));
      }
      if (typeof body.detail === 'string' && body.detail.length > 0) {
        return Promise.reject(new Error(body.detail));
      }
      if (typeof body.title === 'string' && body.title.length > 0) {
        return Promise.reject(new Error(body.title));
      }
    }
    return Promise.reject(err);
  },
);
